import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import * as columns from './filter-samples-grid-header';
import * as eQueryColumns from './equery-samples-grid-header';
import { GridHeader, GridEvent } from '../../tracking-overview/interfaces/grid';
import { GroupTrackingGridData, ColumnInfo } from '../filter-samples-info';
import { UrlBuilder } from '../../../core';
import { SampleTrackingStorage } from '../../sample-tracking-storage';
import { cloneDeep } from 'lodash';
import { IFilterSelectItem } from '../../shared/sample-tracking-select/sample-tracking-select.component';
import { DynamicListViewColumnData, UserConfigInfo, DynamicListColumnInfo } from './../interface/dynamic-list-info';
import { SelectedColumns, AvailableColumns, ColumnSettingInfo } from '../../../shared/covance-grid/column-settings/column-settings';
import { DateRangeFilterValue } from '../../../shared/time-frame-filter/timeframe-filter.component';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { DeleteModalData } from '../../../shared/model-dialog/delete/delete-modal.component';
import { ModalDialogService } from '../../../shared/model-dialog/modal-dialog.service';
import { UserStorage, User } from '../../../user';
import { map } from 'rxjs/operators';
import { CourierModelDialogService } from '../../../shared/courier-model/courier-model-dialog-service';

@Injectable()
export class SampleColumnsService {
    private isSponsor: boolean;
    private readonly commonColumns = cloneDeep([
        columns.protocolColumn,
        columns.regionColumn,
        columns.projectNumberColumn,
        columns.investigatorNumberColumn,
        columns.investigatorNameColumn,
        columns.countryColumn,
        columns.screeningNumberColumn,
        columns.patientNumberColumn,
        columns.collectionDateColumn,
        columns.collectionTimeColumn,
        columns.visitDescriptionColumn,
        columns.protocolVisitCodeColumn,
        columns.accessionNumberColumn,
        columns.containerNumberColumn,
        columns.containerBarcodeColumn, 
        eQueryColumns.specimenTypeColumn,
    ]);

    private readonly endColumns = cloneDeep([
        columns.kitReceiptDateColumn,
        columns.kitReceiptTimeColumn,
        columns.containerReceiptDateColumn,
        columns.containerReceiptTimeColumn,
        columns.expectedReceiptConditionColumn,
        columns.actualReceiptConditionColumn,
        columns.containerLabelLine1Column,
        columns.containerLabelLine2Column,
    ]);

    private readonly basicGridcolumns = cloneDeep([
        ...this.commonColumns,
        columns.sampleStatusColumn,
        ...this.endColumns,
        columns.cancellationDescriptionColumn,
    ]);

    private readonly shippedFromSiteColumns = cloneDeep([
        columns.protocolColumn,
        columns.regionColumn,
        columns.projectNumberColumn,
        columns.investigatorNumberColumn,
        columns.investigatorNameColumn,
        columns.countryColumn,
        columns.screeningNumberColumn,
        columns.patientNumberColumn,
        columns.visitDescriptionColumn,
        columns.protocolVisitCodeColumn,
        columns.accessionNumberColumn,
        columns.containerNumberColumn,
        columns.containerBarcodeColumn,
        columns.containerLabelLine1Column,
        columns.containerLabelLine2Column,
        eQueryColumns.specimenTypeColumn,
        columns.shipmentCreatedDate,
        columns.shipmentCreatedTime,
        columns.shipmentSubmittedDate,
        columns.shipmentSubmittedTime,
        columns.scheduledShipmentDate,
        columns.scheduledShipmentTime,
        columns.siteAirbillNumberColumn,
        columns.shipmentCourier,
        columns.shippedToOrganizationName,
        columns.siteShipmentStatus
    ]);

    private readonly shipmentsOnHoldColumns = cloneDeep([
        columns.protocolColumn,
        columns.regionColumn,
        columns.projectNumberColumn,
        columns.countryColumn,
        columns.investigatorNameColumn,
        columns.investigatorNumberColumn,
        columns.screeningNumberColumn,
        columns.patientNumberColumn,
        columns.accessionNumberColumn,
        columns.collectionDateColumn,
        columns.protocolVisitCodeColumn,
        columns.visitDescriptionColumn,
        columns.kitReceiptDateColumn,
        eQueryColumns.daysWithActiveeQuery,
        eQueryColumns.eQueryType,
        eQueryColumns.numberOfSamplesEffectedByEquery,
        eQueryColumns.eQueryOpenDate,
    ]);

    constructor(
        private urlBuilder: UrlBuilder,
        private storage: SampleTrackingStorage,
        private router: Router,
        private deleteService: ModalDialogService,
        private userStorage: UserStorage,
        private courierService: CourierModelDialogService) {
    }
    /**
     * 
     * @param id 
     * @returns grid header columns
     * add mapping for download report on samples-data-download-service.ts
     */
    public getColumnBySampleId(id: number): GridHeader[] {
        switch (id) {
            case 8:
                return this.shippedFromSiteColumns;
            case 9:
                return this.shipmentsOnHoldColumns;
            default:
                return this.basicGridcolumns;
        }
    }

    public getGridColumnInfo(info: DynamicListViewColumnData) {
        if (!info || !info.dynamicListColumnInfo || info.dynamicListColumnInfo.length === 0) { return null; }
        let configJson: UserConfigInfo[] = info.configJSON ? JSON.parse(info.configJSON) : [];
        let gridColumns: GridHeader[] = [];
        let sortedColumnList = info.dynamicListColumnInfo.sort((a, b) => a.sequence < b.sequence ? -1 : 1);
        sortedColumnList.forEach(column => {
            let userConfigcolumn = this.getConfigJsonMappedColumn(column.appDataColumnDefinitionId, configJson);
            let gridHeader = this.getGridHeaderInfo(column);
            gridHeader.sequence = userConfigcolumn && userConfigcolumn.sequence ? userConfigcolumn.sequence : column.sequence;
            if (userConfigcolumn) {
                gridColumns.push(gridHeader);
            } else if (configJson.length === 0 && (column.defaultFlag || column.mandatoryFlag)) {
                gridColumns.push(gridHeader);
            }
        });
        gridColumns = gridColumns.sort((a, b) => a.sequence < b.sequence ? -1 : 1);
        info.userConfig = configJson;
        return { columnHeader: gridColumns, viewInfo: info, pageSize: info.pageSize };
    }

    public async loadGridColumnOnColumnSettings(columnSettingInfo: ColumnSettingInfo, listcolumnData: DynamicListViewColumnData, columnList: GridHeader[]) {
        if (columnSettingInfo.resetSettings) {
            return await this.showResetDialogConfirmation(listcolumnData, columnList);
        }
        let gridColumns: GridHeader[] = [];
        let configData: UserConfigInfo[] = [];
        columnSettingInfo.selectedColumns.forEach((sc, index) => {
            let columnData = listcolumnData.dynamicListColumnInfo.find(t => t.appDataColumnDefinitionId === +sc.key);
            let gridHeader = this.getGridHeaderInfo(columnData, index + 1);
            gridHeader.sequence = index + 1;
            gridColumns.push(gridHeader);
            configData.push({
                appDataColumnDefinitionId: columnData.appDataColumnDefinitionId,
                sequence: index + 1,
                isDefault: true
            });
        });
        gridColumns = gridColumns.sort((a, b) => a.sequence < b.sequence ? -1 : 1);
        this.storage.saveUserConfigRequest(configData, listcolumnData.appDataDefinitionId);
        return {
            columns: gridColumns, userConfig: configData, isDeleted: false
        };
    }

    public resetColumnSettings(info: DynamicListViewColumnData) {
        if (!info || !info.dynamicListColumnInfo || info.dynamicListColumnInfo.length === 0) { return null; }
        let gridColumns: GridHeader[] = [];
        let sortedColumnList = info.dynamicListColumnInfo.sort((a, b) => a.sequence < b.sequence ? -1 : 1);
        sortedColumnList.forEach(column => {
            let gridHeader = this.getGridHeaderInfo(column);
            if ((column.defaultFlag || column.mandatoryFlag) && !column.hiddenFlag) {
                gridColumns.push(gridHeader);
            }
        });
        gridColumns = gridColumns.sort((a, b) => a.sequence < b.sequence ? -1 : 1);
        this.storage.resetColumnSettings(info.appDataDefinitionId);
        return { columns: gridColumns, isDeleted: true };
    }

    public async showResetDialogConfirmation(info: DynamicListViewColumnData, gridColumns: GridHeader[]) {
        let data: DeleteModalData = {
            title: 'Confirmation',
            content: 'This will reset columns back to their default order',
            closeButtonText: 'Cancel',
            deleteButtonText: 'Apply',
            reverseButtons: true, 
            displayClose: false
        };
        let result = await this.deleteService.showCustomDeleteDialog(data).toPromise();
        if (result && result.isDeleted) {
            return this.resetColumnSettings(info);
        }
        return { columns: gridColumns, isDeleted: false };
    }

    public loadGridColumnOnColumnSwap(columnList: GridHeader[], listcolumnData: DynamicListViewColumnData) {
        let configData: UserConfigInfo[] = [];
        columnList.forEach((sc, index) => {
            let columnData = listcolumnData.dynamicListColumnInfo.find(t => t.appDataColumnDefinitionId === sc.appDataColumnId);
            configData.push({
                appDataColumnDefinitionId: columnData.appDataColumnDefinitionId,
                sequence: index + 1,
                isDefault: true
            });
        });
        this.storage.saveUserConfigRequest(configData, listcolumnData.appDataDefinitionId);
        return configData;
    }

    public isColumnSwapped(columnList: GridHeader[], updatedGridHeader: GridHeader[]) {
        return columnList.map(t => t.key).toString() !== updatedGridHeader.map(t => t.key).toString();
    }

    public isColumnUpdated(currentRecord: ColumnSettingInfo, updatedRecord: ColumnSettingInfo) {
        if (!updatedRecord.availableColumns || !updatedRecord.selectedColumns) { return true; }
        if (currentRecord.availableColumns.length !== updatedRecord.availableColumns.length) { return true; }
        if (currentRecord.selectedColumns.length !== updatedRecord.selectedColumns.length) { return true; }
        if (currentRecord.availableColumns.map(t => t.key).toString() !==
            updatedRecord.availableColumns.map(t => t.key).toString()) { return true; }
        if (currentRecord.selectedColumns.map(t => t.key).toString() !==
            updatedRecord.selectedColumns.map(t => t.key).toString()) { return true; }
        return false;
    }

    public getColumnSettingInfo(columnDefs: GridHeader[], info: DynamicListViewColumnData): ColumnSettingInfo {
        let columnOptions = info.dynamicListColumnInfo.filter(item => columnDefs.findIndex(t => t.appDataColumnId === item.appDataColumnDefinitionId) < 0);
        let selectedcolumns: SelectedColumns[] = columnDefs.map((t, index) => {
            let columnInfo = info.dynamicListColumnInfo.find(column => column.appDataColumnDefinitionId === t.appDataColumnId);
            return {
                displayName: t.displayText,
                key: columnInfo.appDataColumnDefinitionId.toString(),
                isMandatory: columnInfo ? columnInfo.mandatoryFlag : false,
                sequence: index + 1
            };
        });
        let availableColumns: AvailableColumns[] = [];
        columnOptions.forEach(column => {
            if (!column.hiddenFlag) {
                let columnInfo: AvailableColumns = {
                    displayName: column.columnNameDisplay,
                    key: column.appDataColumnDefinitionId.toString()
                };
                availableColumns.push(columnInfo);
            }
        });
        availableColumns = availableColumns.sort((a, b) => a.displayName.localeCompare(b.displayName));
        return {
            availableColumns: availableColumns,
            selectedColumns: selectedcolumns,
            showReset: true
        };
    }

    public applySearch(term: string, formattedData: GroupTrackingGridData[], gridHeader: GridHeader[]) {
        if (!term || !formattedData) {
            return formattedData;
        }
        term = term.trim().toLowerCase();
        let filterData: GroupTrackingGridData[] = [];
        for (var rowData of formattedData) {
            for (var col of gridHeader) {
                if (`${rowData[col.key as keyof GroupTrackingGridData]}`.toLowerCase().indexOf(term) !== -1) {
                    filterData.push(rowData);
                    break;
                }
            }
        }
        return filterData;
    }

    public onGridItemClick(selectedItem: GridEvent, gridData: GroupTrackingGridData[]) {
        let data = {
            accessionNumber: selectedItem.data.accessionNumber,
            containerNumber: selectedItem.data.containerNumber,
            shipmentID: selectedItem.data.lgxCourierShipmentId
        };
        if (selectedItem.header.key === 'accessionNumber') {
            data.containerNumber = null;
        }
        if (selectedItem.header.key === 'shipmentStatus') {
            this.userStorage.currentUser.pipe(
                map(user => user.isSponsor)
            ).subscribe(isSponsor => {
                this.isSponsor = isSponsor;
            });
            this.courierService.showCourierDetailsDialog(data.shipmentID, selectedItem.data.shipmentStatus, null, this.isSponsor);
            return;
        }
        this.storage.sampleSearchData = new Observable(ob => ob.next(data));
        this.storage.clearGridData();
        if (selectedItem.header.key === 'accessionNumber') {
            this.urlBuilder.esa().sampletracking().visitDetails().navigate();
            return;
        }
        this.urlBuilder.esa().sampletracking().sampleDetails(data.containerNumber).navigate();
    }

    public onGridItemClickReceivedList(selectedItem: GridEvent, columnInfo: DynamicListViewColumnData, gridData: GroupTrackingGridData[]) {
        if (!selectedItem.data[selectedItem.header.key]) { return; }
        let columnKey = selectedItem.header.isTime ? selectedItem.header.key.replace('_time', '') : selectedItem.header.key;
        let columnData = columnInfo.dynamicListColumnInfo.find(info => info.dbColumnName === columnKey && info.columnNameDisplay === selectedItem.header.displayText);
        if (!columnData || !columnData.hyperlinkFlag || !columnData.hyperlinkRoute) { return; }
        this.storage.gridData = of(gridData);
        this.storage.gridColumnData = of(columnInfo);
        let data = {
            accessionNumber: selectedItem.data.accessionnumber,
            containerNumber: selectedItem.data.container_number,
        };
        let dbColumnData: string = selectedItem.data[columnData.actionDbViewColumnName as keyof GroupTrackingGridData];
        if (columnData.actionDbViewColumnName && !dbColumnData && !dbColumnData.length) { return; }
        this.storage.sampleSearchData = new Observable(ob => ob.next(data));
        let routeUrl = columnData.hyperlinkRoute.trim().replace('{0}', selectedItem.data[columnData.actionDbViewColumnName as keyof GroupTrackingGridData]);
        this.router.navigateByUrl(routeUrl);
    }

    public getDateColumns(id: number): IFilterSelectItem[] {
        let filteredColumns = this.getColumnBySampleId(id);
        filteredColumns = filteredColumns.filter(t => t.isDate);
        if (!filteredColumns || filteredColumns.length === 0) { return []; }
        return filteredColumns.map((column, index) => {
            return {
                id: index + 1,
                value: column.displayText,
                key: column.key,
                checked: false
            };
        }).sort((a, b) => a.value.localeCompare(b.value));
    }

    public getDateColumnsByColumnFilter(info: DynamicListColumnInfo[]) {
        let dateColumns = info.filter(t => t.dataType === 'date' && !t.hiddenFlag);
        if (!dateColumns || !dateColumns.length) { return []; }
        return dateColumns.map((column, index) => {
            return {
                id: index + 1,
                value: column.columnNameDisplay,
                key: column.physicalEntityAttribute ? column.physicalEntityAttribute : column.dbColumnName,
                checked: false
            };
        }).sort((a, b) => a.value.localeCompare(b.value));
    }

    public getDefaultTimeFrameOption(options: IFilterSelectItem[], statusID: number) {
        if (statusID === 9) {
            return options.filter(t => t.key === 'eQueryOpenDate');
        } else if (statusID === 8) {
            return options.filter(t => t.key === 'investigatorsiteshipment_shipmentplannedfor');
        } else if (statusID === 10) {
            return options.filter(t => t.key === 'sampleregistrationcollectiondate');
        }
        return options.filter(t => t.key === 'kit_received_on' || t.key === 'kitReceivedDate');
    }

    public getDynamicColumnList(columnDefs: GridHeader[], dynamicColumnList: DynamicListColumnInfo[]) {
        let joinColumns = dynamicColumnList.filter(t => t.physicalEntity && t.physicalEntityAttribute);
        let customSqlColumns = dynamicColumnList.filter(t => t && t.customSQL);
        let filteredColumns = this.getFilteredSelectedColumnList(joinColumns, customSqlColumns, columnDefs);
        let columnList = [
            ...filteredColumns.map(t => t.key.replace('_time', '')),
            ...dynamicColumnList.filter(t => t.hiddenFlag).map(t => t.dbColumnName)
        ];
        let joinFilters: ColumnInfo[] = this.getColumnInfoList(joinColumns, columnDefs);
        let customColumns: ColumnInfo[] = this.getColumnInfoList(customSqlColumns, columnDefs);
        return { columns: columnList, joinColumns: joinFilters, customColumns: customColumns };
    }

    public getMonthlyRange(): DateRangeFilterValue {
        return {
            dateBegin: moment().subtract(30, 'd').format('YYYY-MM-DD'),
            dateEnd: moment().format('YYYY-MM-DD'),
            rangeType: 'monthly'
        };
    }

    private getConfigJsonMappedColumn(columnId: number, configJson: UserConfigInfo[]) {
        if (!columnId || !configJson || !configJson.length) { return null; }
        let info = configJson.filter(t => t.appDataColumnDefinitionId === columnId);
        if (info && info.length > 0) { return info[0]; }
        return null;
    }

    private getGridHeaderInfo(columnData: DynamicListColumnInfo, sequence?: number) {
        let dbColumnName = columnData.dbColumnName;
        if (columnData.physicalEntity && columnData.physicalEntityAttribute) {
            dbColumnName = columnData.physicalEntityAttribute;
        }
        if (columnData.customSQL) {
            dbColumnName = columnData.entityAttribute;
        }
        let gridHeader: GridHeader = {
            appDataColumnId: columnData.appDataColumnDefinitionId,
            displayText: columnData.columnNameDisplay,
            key: dbColumnName.toLowerCase(),
            enableFilter: true,
            enableSearch: true,
            skipConversion: true,
            isDate: columnData.dataType === 'date',
            isTime: columnData.dataType === 'time',
            isDateTime: columnData.dataType === 'datetime',
            isLink: columnData.hyperlinkFlag ? true : false,
            sequence: sequence ? sequence : columnData.sequence,
            sort: null
        };
        if (gridHeader.isTime) {
            gridHeader.key = gridHeader.key + '_time';
        }
        if (columnData.formatString) {
            gridHeader.defaultDateTimeFormat = columnData.formatString;
        }
        return gridHeader;
    }

    private getFilteredSelectedColumnList(joinColumns: DynamicListColumnInfo[], customColumns: DynamicListColumnInfo[], gridColumns: GridHeader[]) {
        let filteredColumns = gridColumns;
        if (joinColumns && joinColumns.length) {
            filteredColumns = gridColumns.filter(t => !joinColumns.find(col => col.appDataColumnDefinitionId === t.appDataColumnId));
        }
        if (customColumns && customColumns.length) {
            filteredColumns = gridColumns.filter(t => !customColumns.find(col => col.appDataColumnDefinitionId === t.appDataColumnId));
        }
        return filteredColumns;
    }

    private getColumnInfoList(columnsList: DynamicListColumnInfo[], gridColumns: GridHeader[]) {
        if (!columnsList || !columnsList.length) { return []; }
        let selectedCustomColumns = columnsList.filter(t => gridColumns.find(col => col.appDataColumnId === t.appDataColumnDefinitionId));
        return selectedCustomColumns.map(col => {
            return {
                dbViewColumnName: col.dbColumnName,
                displayName: col.columnNameDisplay,
                physicalEntity: col.physicalEntity,
                physicalEntityAttribute: col.physicalEntityAttribute ? col.physicalEntityAttribute.toLowerCase() : '',
                entityAttribute: col.entityAttribute ? col.entityAttribute.toLowerCase() : '',
                customSQL: col.customSQL
            };
        });
    }
}